import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  RevButton,
  RevCard,
  RevDragAndDrop,
  RevInput,
  RevSelect,
  RevTextArea,
} from "@revmatics/catalyst";
import { ProfileDataType } from "../../../stores/types/profileTypes.tsx";
import {
  ContactUsBodyType,
  SubjectsDataType,
} from "../../../stores/types/helpTypes.tsx";
import { useAppDispatch, useAppSelector } from "../../../stores/hooks/hooks.ts";
import { setContactUs } from "../../../stores/thunks/helpThunk.tsx";
import { displayAlert } from "../../../stores/slice/alertSlice.tsx";
import { useEffect, useMemo, useState } from "react";
import { UploadFileCard } from "./components/uploadFileCard/UploadFileCard.tsx";

const MAX_SIZE_BYTES = 1 * 1024 * 1024;

type Props = {
  profile: ProfileDataType;
  subjects: SubjectsDataType;
  showTitle?: boolean;
  useCardWrapper?: boolean;
  showSubmitButton?: boolean;
  formId?: string;
  onSuccess?: () => void;
  selectedSubjectId?: string;
};

export const ContactUsPage = ({
  profile,
  subjects,
  showTitle = true,
  useCardWrapper = true,
  showSubmitButton = true,
  formId = "contact-us-form",
  onSuccess,
  selectedSubjectId,
}: Props) => {
  const dispatch = useAppDispatch();
  const [submitted, setSubmitted] = useState(false);
  const [files, setFiles] = useState(new Set<File>());
  const [failedFiles, setFailedFiles] = useState(new Set<File>());
  const [loadingFiles, setLoadingFiles] = useState<Set<File>>(new Set());
  const { currentBrand, brandList } = useAppSelector((state) => state.brand);

  // Track individual progress percentages for each file
  const [fileProgress, setFileProgress] = useState<Map<File, number>>(
    new Map(),
  );

  const methods = useForm<ContactUsBodyType>({
    mode: "onSubmit",
    defaultValues: {
      email: profile.email || "",
      subject: selectedSubjectId || "",
      message: "",
      files: [],
    },
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = methods;

  const subjectValue = watch("subject");

  const currentBrandData = useMemo(() => {
    if (!currentBrand || !brandList || brandList.length === 0) return;

    return brandList.find((item) => item.id.toString() === currentBrand);
  }, [brandList, currentBrand]);

  useEffect(() => {
    if (subjectValue === "ADD_COMPANY") {
      const message = `First Name: ${profile.first_name || ""}
Last Name: ${profile.last_name || ""}
Company Admin Email: ${profile.email || ""}
Company Name: ${""}
Brand Name: ${currentBrandData?.name || ""}
Brand Website: ${currentBrandData?.website || ""}`;

      setValue("message", message);
    } else {
      setValue("message", "");
    }
  }, [
    subjectValue,
    profile,
    setValue,
    currentBrandData?.name,
    currentBrandData?.website,
  ]);

  const onFilesChange = (incomingFiles: File[]) => {
    const validFiles: File[] = [];
    const tooLargeFiles: File[] = [];

    incomingFiles.forEach((file) => {
      if (file.size > MAX_SIZE_BYTES) {
        tooLargeFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    if (tooLargeFiles.length > 0) {
      setFailedFiles((prev) => new Set([...prev, ...tooLargeFiles]));
    }

    if (validFiles.length > 0) {
      setFiles((prev) => new Set([...prev, ...validFiles]));
      setLoadingFiles((prev) => new Set([...prev, ...validFiles]));

      // simulation for visualization of file progress
      validFiles.forEach((file) => {
        let currentProgress = 0;
        const interval = setInterval(() => {
          currentProgress += Math.floor(Math.random() * 15) + 5;

          if (currentProgress >= 100) {
            currentProgress = 100;
            clearInterval(interval);
            setLoadingFiles((prev) => {
              const next = new Set(prev);
              next.delete(file);
              return next;
            });
          }

          setFileProgress((prev) => new Map(prev).set(file, currentProgress));
        }, 150);
      });
    }
  };

  const onSubmit = async (data: ContactUsBodyType) => {
    const formDataForApi = new FormData();
    Array.from(files).forEach((item) =>
      formDataForApi.append("attachments", item),
    );
    Object.keys(data).forEach((item) => {
      const key = item as keyof typeof data;
      if (key !== "files") {
        formDataForApi.append(key, data[key]);
      }
    });
    try {
      await dispatch(setContactUs(formDataForApi)).unwrap();
      dispatch(
        displayAlert({
          type: "success",
          text: "Your message has been sent successfully!",
        }),
      );
      reset();
      setFailedFiles(new Set());
      setFiles(new Set());
      setFileProgress(new Map());
      setSubmitted(true);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to submit contact form:", error);
    }
  };

  const confirmationContent = (
    <div className="flex flex-col gap-4 w-full">
      <h5 className="text-monochrome-900 mb-4">Message sent!</h5>
      <p className="typography-body-sm text-monochrome-600">
        We've received your message and will get back to you shortly.
      </p>
      <RevButton
        variant="secondary"
        text="Send another message"
        handleClick={() => setSubmitted(false)}
      />
    </div>
  );

  const formContent = (
    <>
      {showTitle && <h5 className="text-monochrome-900 mb-4">Contact Us</h5>}
      <FormProvider {...methods}>
        <form
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6 w-full"
          noValidate
        >
          <RevInput
            name="email"
            label="Your Email"
            type="text"
            size="md"
            register={register("email", { required: "Email is required." })}
            error={errors.email}
            disabled
          />
          <RevSelect
            name="subject"
            label="Subject"
            options={subjects.map((item) => ({
              value: item[0],
              label: item[1],
            }))}
            control={control}
            required
            error={errors.subject}
            size="md"
            placeholder="Select"
          />
          <RevTextArea
            name="message"
            label="Message"
            size="md"
            placeholder="Write your message here"
            rows={6}
            required
            register={register("message", { required: "Message is required." })}
            error={errors.message}
          />
          <Controller
            name="files"
            control={control}
            render={({ fieldState }) => {
              return (
                <RevDragAndDrop
                  name="file_input"
                  showUploadButton
                  displayIcon={false}
                  onFilesChange={(newFiles) => {
                    if (newFiles) {
                      onFilesChange(newFiles);
                    }
                  }}
                  error={fieldState.error}
                  btnTitle="or drag and drop"
                  dropzoneClassName="!h-[143px] !py-6"
                  multiple
                  disabled={files.size >= 10}
                  maxFiles={10}
                  label="Attach files"
                  fullWidth
                  btnVersion
                  maxSizeBytes={null}
                  accept={{
                    "image/jpeg": [".jpeg"],
                    "image/jpg": [".jpg"],
                    "image/png": [".png"],
                  }}
                />
              );
            }}
          />
          <div className="space-y-2">
            {Array.from(files).map((file, index) => {
              return (
                <UploadFileCard
                  key={`upload-file-${index}`}
                  isLoading={loadingFiles.has(file)}
                  progress={fileProgress.get(file) || 0}
                  file={file}
                  handleRemove={() => {
                    setFiles((prev) => {
                      const updatedFiles = new Set(prev);
                      updatedFiles.delete(file);
                      return updatedFiles;
                    });
                    setFileProgress((prev) => {
                      const next = new Map(prev);
                      next.delete(file);
                      return next;
                    });
                  }}
                />
              );
            })}
            {Array.from(failedFiles).map((file, index) => {
              return (
                <UploadFileCard
                  key={`failed-upload-file-${index}`}
                  isLoading={loadingFiles.has(file)}
                  file={file}
                  failed
                  handleRemove={() =>
                    setFailedFiles((prev) => {
                      const updatedFiles = new Set(prev);
                      updatedFiles.delete(file);
                      return updatedFiles;
                    })
                  }
                />
              );
            })}
          </div>
          {showSubmitButton && <RevButton type="submit" text="Send Message" />}
        </form>
      </FormProvider>
    </>
  );

  if (useCardWrapper) {
    return (
      <RevCard padding="pt-4 px-6 pb-6" className="max-w-[560px] mx-auto">
        {submitted ? confirmationContent : formContent}
      </RevCard>
    );
  }

  return submitted ? confirmationContent : formContent;
};
